import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { Section, SectionTitle, CTAButton } from './Shared';
import { volumes, bonuses, valueSummary, PRICE } from '../data/pack';
const Disclosure = ({
  label,
  children,
  className = ''
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  return <div className={className} data-id="element-124" data-mp="dvx70uzgvbi2">
      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} className="inline-flex items-center gap-1.5 font-display text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-goldDark transition-colors duration-200 ease-out hover:text-brand-burgundy" data-id="element-125" data-mp="isnkdajvzszs">
        {label}
        <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-200 ease-out ${open ? 'rotate-180' : ''}`} data-id="element-126" data-mp="wrwr7jm7dar3" />
      </button>
      <AnimatePresence initial={false} data-id="element-127" data-mp="3p6la6tdcpjpz">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="overflow-hidden" data-id="element-128" data-mp="3p51ub3vlxaoq">
            <div className="pt-3" data-id="element-129" data-mp="3u4mqj6je74xr">{children}</div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
const VolumeBlock = () => <div className="mx-auto mb-14 max-w-5xl space-y-4" data-id="element-130" data-mp="6u6kdgzywaza">
    {volumes.map(vol => <article key={vol.n} className="paper-panel grid grid-cols-[86px_1fr] gap-4 rounded-xl p-4 text-left md:grid-cols-[136px_1fr] md:gap-6 md:p-5" data-id="element-131" data-mp="lr7fluhdao2i">
        <img src={vol.cover} alt={`${vol.label} — ${vol.title}`} loading="lazy" className="w-full self-start rounded-sm ring-1 ring-brand-goldDark/25" data-id="element-132" data-mp="gpqcdsbeupir" />
        <div className="min-w-0" data-id="element-133" data-mp="38738e2kwetk0">
          <span className={`font-display text-[9px] font-semibold uppercase tracking-[0.24em] ${vol.accent}`} data-id="element-134" data-mp="387v7hndpf9f1">
            {vol.label}
          </span>
          <h3 className={`mt-1 font-serif text-[17px] font-semibold leading-tight md:text-2xl ${vol.accent}`} data-id="element-135" data-mp="3h81fitqmvh6t">
            {vol.title}
          </h3>
          <p className="mt-1 font-display text-[9px] font-semibold uppercase leading-snug tracking-[0.14em] text-brand-goldDark md:text-[10px]" data-id="element-136" data-mp="3m4rufdqpc6qn">
            {vol.subtitle}
          </p>
          <p className="mt-2 text-[13px] leading-snug text-brand-text md:text-[15px]" data-id="element-137" data-mp="3v5q163siyscx">
            {vol.intro}
          </p>

          <Disclosure label="Inside this volume" className="mt-3" data-id="element-138" data-mp="8ryirj127cx9">
            <ul className="grid grid-cols-1 gap-x-8 gap-y-1.5 sm:grid-cols-2" data-id="element-139" data-mp="8rga1mx7n68g">
              {vol.features.map(f => <li key={f} className="flex items-start gap-2 text-brand-text" data-id="element-140" data-mp="dp834k9lcye8">
                  <span aria-hidden="true" className="mt-[6px] h-1 w-1 shrink-0 rounded-full bg-brand-gold" data-id="element-141" data-mp="iqdxemiwgtpv" />
                  <span className="text-[12.5px] leading-snug md:text-sm" data-id="element-142" data-mp="1y8247tkqlcty">
                    {f}
                  </span>
                </li>)}
            </ul>
            <p className="mt-3 font-serif text-[13px] italic text-brand-burgundy md:text-[15px]" data-id="element-143" data-mp="1obfmhdnpj1w5">
              {vol.closing}
            </p>
          </Disclosure>
        </div>
      </article>)}
  </div>;
const BonusBlock = () => <div className="mx-auto max-w-5xl" data-id="element-144" data-mp="2h0w9twwgzsli">
    <div className="text-center" data-id="element-145" data-mp="2h1xoscglwz8i">
      <h3 className="font-display text-[15px] font-semibold uppercase tracking-[0.14em] text-brand-burgundy md:text-xl" data-id="element-146" data-mp="2c837ffoh4wcl">
        5 Practical Bonuses —{' '}
        <span className="text-brand-goldDark" data-id="element-147" data-mp="277xm0h8ss62y">Always Included</span>
      </h3>
      <p className="mx-auto mt-2 max-w-2xl text-[13px] text-brand-textLight/85 md:text-base" data-id="element-148" data-mp="34qgv4mg3j1xy">
        Additional tools designed to make your classes easier to organize,
        personalize and manage.
      </p>
    </div>

    <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 lg:gap-4" data-id="element-149" data-mp="2kishcj8ais2l">
      {bonuses.map(bonus => <article key={bonus.n} className="flex h-full flex-col text-left" data-id="element-150" data-mp="2aii26t2p3aks">
          <div className="relative" data-id="element-151" data-mp="2akmhe761nw0d">
            <img src={bonus.cover} alt={`${bonus.n} — ${bonus.title}`} loading="lazy" className="w-full rounded-sm ring-1 ring-brand-goldDark/25" data-id="element-152" data-mp="2fgjl9e04f9w9" />
            <span className="absolute right-0 top-0 bg-brand-gold px-1.5 py-0.5 font-display text-[8px] font-bold uppercase tracking-[0.12em] text-brand-ink" data-id="element-153" data-mp="384p5lzn9iwvk">
              Free
            </span>
          </div>
          <h4 className="mt-2.5 font-serif text-[14px] font-semibold leading-tight text-brand-burgundy md:text-[15px]" data-id="element-154" data-mp="2yeme63vshqzc">
            {bonus.title}
          </h4>
          <p className="mt-1 text-[12px] leading-snug text-brand-text" data-id="element-155" data-mp="3qx1j9ijd9kl0">
            {bonus.intro}
          </p>
          <Disclosure label="Includes" className="mt-2" data-id="element-156" data-mp="1fgrb1xutr7sk">
            <ul className="space-y-1" data-id="element-157" data-mp="1ff6e61l1fcrj">
              {bonus.includes.map(item => <li key={item} className="flex items-start gap-1.5 text-[12px] leading-snug text-brand-text" data-id="element-158" data-mp="1agbvr0qzfq7y">
                  <span aria-hidden="true" className="mt-[6px] h-1 w-1 shrink-0 rounded-full bg-brand-gold" data-id="element-159" data-mp="1k9kcabr33xzw" />
                  {item}
                </li>)}
            </ul>
          </Disclosure>
        </article>)}
    </div>
  </div>;
const ValueSummary = () => <div className="mx-auto mt-14 max-w-3xl" data-id="element-160" data-mp="15h0d1m722anq">
    <div className="paper-panel rounded-3xl p-6 text-center md:p-10" data-id="element-161" data-mp="11hi13fx7p9x1">
      <h3 className="font-display text-lg font-bold uppercase tracking-[0.14em] text-brand-burgundy md:text-2xl" data-id="element-162" data-mp="2gy8go59vnmqu">
        Everything You Receive Today
      </h3>
      <ul className="mt-6 grid grid-cols-1 gap-x-8 gap-y-2 text-left sm:grid-cols-2" data-id="element-163" data-mp="2cwn8uyc79yni">
        {valueSummary.map(item => <li key={item} className="flex items-start gap-2.5" data-id="element-164" data-mp="1el3v78ypjtlo">
            <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand-goldDark" data-id="element-165" data-mp="1ejj1flk7weov" />
            <span className="text-sm text-brand-text md:text-[15px]" data-id="element-166" data-mp="1jl8awwrskkau">
              {item}
            </span>
          </li>)}
      </ul>

      <p className="mt-7 font-serif text-2xl font-semibold text-brand-burgundy md:text-3xl" data-id="element-167" data-mp="1njftlx5tfvoo">
        Complete Bundle: Only {PRICE}
      </p>

      <div className="mt-5" data-id="element-168" data-mp="vtta58fnyb1p">
        <CTAButton className="w-full px-6 py-4 text-[12px] sm:w-auto md:px-10 md:text-sm" data-id="element-169" data-mp="vv4dx893zist">
          Yes, I Want the Complete Bundle
        </CTAButton>
      </div>

      <p className="mt-4 text-xs text-brand-muted md:text-sm" data-id="element-170" data-mp="14n66ylgulwt2">
        One secure payment · No subscription · Instant access
      </p>
    </div>
  </div>;
export const PackContents = () => {
  return <Section id="whats-inside" className="py-10 md:py-16" data-id="element-171" data-mp="c5tylvd9yzv8">
      <SectionTitle eyebrow="What’s Inside" lead="A structured, highly visual collection created to help you prepare, organize and confidently lead Qigong sessions." className="mb-8" data-id="element-172" data-mp="c49425qyngxd">
        Your Complete Professional Qigong Library
        <span className="mt-3 block font-display text-sm font-semibold uppercase tracking-[0.16em] text-brand-goldDark md:text-base" data-id="element-173" data-mp="3rq8wvsxgm8y7">
          3 Professional Volumes + 5 Free Bonuses
        </span>
      </SectionTitle>

      <VolumeBlock data-id="element-174" data-mp="3hvpcyj7tqvfo" />
      <BonusBlock data-id="element-175" data-mp="3d2w53mh276zd" />
      <ValueSummary data-id="element-176" data-mp="3d2nu604hybqt" />
    </Section>;
};